import React, { useState, useRef, useEffect } from 'react';
import { InteractionMode } from '../types';

interface ControlDockProps {
  onOpenSoundLab: () => void;
  currentPresetName: string;
  presetColor?: string;
  isRecording: boolean;
  onToggleRecording: () => void;
  isCameraActive: boolean;
  onToggleCamera: () => void;
  onOpenMotionGuide: () => void;
  isZenMode: boolean;
  onToggleZenMode: () => void;
  isMobile: boolean;
  interactionMode: InteractionMode;
  isSoundLabOpen: boolean;
  motionSensitivity: number;
  onChangeSensitivity: (val: number) => void;
}

export const ControlDock: React.FC<ControlDockProps> = ({
  onOpenSoundLab,
  currentPresetName,
  presetColor = '#38bdf8',
  isRecording,
  onToggleRecording,
  isCameraActive,
  onToggleCamera,
  onOpenMotionGuide,
  isZenMode,
  onToggleZenMode,
  isMobile,
  interactionMode,
  isSoundLabOpen,
  motionSensitivity,
  onChangeSensitivity
}) => {
  const [isSensitivityOpen, setIsSensitivityOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerBtnRef = useRef<HTMLButtonElement>(null);

  // Close popover on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        isSensitivityOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerBtnRef.current &&
        !triggerBtnRef.current.contains(e.target as Node)
      ) {
        setIsSensitivityOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSensitivityOpen) {
        setIsSensitivityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSensitivityOpen]);

  // Hide dock during full distraction-free zen mode or when the sound lab modal drawer is open
  if (isZenMode || isSoundLabOpen) return null;

  // Describe the current sensitivity state
  const getSensitivityLabel = (val: number) => {
    if (val <= 0.7) return { text: 'Subdued & Gentle', desc: 'Smoothes micro-tremors for calm, sustained ambient textures' };
    if (val <= 1.2) return { text: 'Balanced (1:1)', desc: 'Standard physical reach with natural musical expressiveness' };
    if (val <= 1.7) return { text: 'Dynamic', desc: 'Amplified response: subtle gestures trigger full octave shifts' };
    return { text: 'Expressive', desc: 'Hyper-responsive: light movements glide across high & low registers' };
  };

  const currentInfo = getSensitivityLabel(motionSensitivity);

  return (
    <nav 
      aria-label="Audio and performance controls"
      className="fixed bottom-2.5 sm:bottom-6 left-0 right-0 z-40 px-2 sm:px-6 pointer-events-none flex justify-center pb-[max(env(safe-area-inset-bottom,0px),0.25rem)] animate-fade-in"
    >
      <div className="relative pointer-events-auto flex flex-col items-center">
        {/* Floating Sensitivity Popover Panel */}
        {isSensitivityOpen && (
          <div 
            ref={popoverRef}
            id="motion-sensitivity-popover"
            className="absolute bottom-full mb-3 w-[290px] sm:w-[340px] bg-slate-950/95 backdrop-blur-2xl border border-amber-500/30 rounded-2xl p-4 shadow-2xl text-white animate-scale-in z-50 pointer-events-auto"
          >
            {/* Popover Header */}
            <div className="flex items-center justify-between pb-2.5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="text-amber-400 text-sm">⚡</span>
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-200">
                  Motion Sensitivity
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  {motionSensitivity.toFixed(1)}×
                </span>
                <button
                  type="button"
                  onClick={() => setIsSensitivityOpen(false)}
                  className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition"
                  aria-label="Close sensitivity controls"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Description Card */}
            <div className="py-2.5">
              <div className="text-xs font-bold text-amber-300 mb-0.5">
                {currentInfo.text}
              </div>
              <p className="text-[11px] text-slate-400 leading-snug">
                {currentInfo.desc}
              </p>
            </div>

            {/* Continuous Slider Control */}
            <div className="space-y-1.5 py-1">
              <div className="flex justify-between items-center text-[10px] font-mono text-slate-400 uppercase">
                <span>0.4× Subdued</span>
                <span className="text-amber-400 font-bold">{motionSensitivity.toFixed(2)}×</span>
                <span>2.2× Expressive</span>
              </div>
              <input
                id="motion-sensitivity-slider"
                type="range"
                min="0.4"
                max="2.2"
                step="0.05"
                value={motionSensitivity}
                onChange={(e) => onChangeSensitivity(parseFloat(e.target.value))}
                className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                aria-label="Adjust motion sensitivity slider"
              />
            </div>

            {/* Preset Quick Pointers */}
            <div className="grid grid-cols-4 gap-1.5 pt-2.5 pb-1">
              {[
                { val: 0.6, label: '0.6×', name: 'Gentle' },
                { val: 1.0, label: '1.0×', name: 'Normal' },
                { val: 1.5, label: '1.5×', name: 'Dynamic' },
                { val: 2.0, label: '2.0×', name: 'Expressive' }
              ].map((p) => {
                const isActive = Math.abs(motionSensitivity - p.val) < 0.05;
                return (
                  <button
                    key={p.val}
                    type="button"
                    onClick={() => onChangeSensitivity(p.val)}
                    className={`py-1.5 px-1 rounded-xl text-center transition active:scale-95 border flex flex-col items-center ${
                      isActive 
                        ? 'bg-amber-500/25 border-amber-400/60 text-amber-300 shadow-sm' 
                        : 'bg-slate-900/80 hover:bg-slate-800 border-white/10 text-slate-300 hover:text-white'
                    }`}
                  >
                    <span className="font-mono text-xs font-bold leading-tight">{p.label}</span>
                    <span className="text-[9px] text-slate-400 leading-none mt-0.5">{p.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Reset to 1.0x if modified */}
            {motionSensitivity !== 1.0 && (
              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => onChangeSensitivity(1.0)}
                  className="text-[11px] text-slate-400 hover:text-amber-300 transition flex items-center gap-1 cursor-pointer"
                >
                  <span>↺ Reset to Default (1.0×)</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bottom Control Dock Main Bar */}
        <div 
          id="main-control-dock"
          className="bg-slate-950/95 backdrop-blur-2xl border border-white/20 rounded-full shadow-2xl shadow-black/80 px-2 sm:px-3 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 max-w-[calc(100vw-1rem)] sm:max-w-fit overflow-x-auto no-scrollbar"
        >
          {/* Sound Lab Trigger Button */}
          <button
            id="sound-lab-trigger-btn"
            onClick={onOpenSoundLab}
            className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 rounded-full bg-indigo-600/30 hover:bg-indigo-600/40 border border-indigo-400/40 text-white transition-all duration-200 active:scale-95 group shadow-sm shrink-0 min-h-[44px] cursor-pointer"
            title={`Open Sound Library (Current: ${currentPresetName})`}
            aria-label={`Open Sound Library. Current sound: ${currentPresetName}`}
          >
            <span 
              className="w-2.5 h-2.5 rounded-full shrink-0 animate-pulse shadow-sm"
              style={{ backgroundColor: presetColor }}
            />
            <div className="flex flex-col text-left">
              <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300 leading-none">
                Sound Lab
              </span>
              <span className="text-xs sm:text-sm font-bold text-white max-w-[72px] xs:max-w-[100px] sm:max-w-[140px] md:max-w-[180px] truncate leading-tight mt-0.5">
                {currentPresetName}
              </span>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={2} 
              stroke="currentColor" 
              className="w-4 h-4 text-indigo-300 group-hover:text-white transition shrink-0 hidden xs:block"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </button>

          {/* Primary Record Action Button - Displayed ONLY when Camera Mode is INACTIVE */}
          {!isCameraActive && (
            <>
              <div className="h-6 w-px bg-white/15 shrink-0 hidden xs:block" />
              <button
                id="record-toggle-btn"
                onClick={onToggleRecording}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full transition-all duration-200 active:scale-95 shadow-md border text-xs sm:text-sm font-bold shrink-0 min-h-[44px] cursor-pointer ${
                  isRecording
                    ? 'bg-red-600 hover:bg-red-500 text-white border-red-400 animate-pulse shadow-red-600/30'
                    : 'bg-slate-900/90 hover:bg-slate-800 text-slate-200 hover:text-white border-white/15'
                }`}
                title={isRecording ? "Stop and save recording to library" : "Record session audio"}
                aria-label={isRecording ? "Stop and save recording" : "Record audio"}
              >
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isRecording ? 'bg-white' : 'bg-red-500'}`} />
                <span className="whitespace-nowrap font-bold">
                  {isRecording ? (
                    <>
                      <span className="sm:hidden">Stop</span>
                      <span className="hidden sm:inline">Stop & Save</span>
                    </>
                  ) : (
                    'Record'
                  )}
                </span>
              </button>
            </>
          )}

          {/* Divider */}
          <div className="h-6 w-px bg-white/15 shrink-0 hidden sm:block" />

          {/* Motion Sensitivity Button & Popover Trigger */}
          <button
            ref={triggerBtnRef}
            id="motion-sensitivity-dock-btn"
            onClick={() => setIsSensitivityOpen(!isSensitivityOpen)}
            className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-2 rounded-full backdrop-blur-md transition-all duration-200 active:scale-95 shadow-md border text-xs sm:text-sm font-bold shrink-0 min-h-[44px] cursor-pointer ${
              isSensitivityOpen || motionSensitivity !== 1.0
                ? 'bg-amber-500/25 text-amber-300 border-amber-400/50 shadow-amber-500/20'
                : 'bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white border-white/15'
            }`}
            title={`Adjust Motion Sensitivity (Current: ${motionSensitivity.toFixed(1)}x)`}
            aria-label={`Adjust Motion Sensitivity. Current multiplier: ${motionSensitivity.toFixed(1)}x`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-amber-400 shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
            </svg>
            <span className="whitespace-nowrap font-mono font-bold">
              {motionSensitivity.toFixed(1)}×
            </span>
            <span className="hidden lg:inline whitespace-nowrap text-slate-300 text-xs font-medium">
              Sens
            </span>
          </button>

          {/* Camera Tracker Quick Toggle */}
          <button
            id="camera-quick-toggle-btn"
            onClick={onToggleCamera}
            className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-2 rounded-full backdrop-blur-md transition-all duration-200 active:scale-95 shadow-md border text-xs sm:text-sm font-bold shrink-0 min-h-[44px] min-w-[44px] cursor-pointer ${
              isCameraActive
                ? 'bg-sky-500/30 text-sky-300 border-sky-400/50 shadow-sky-500/20'
                : 'bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white border-white/15'
            }`}
            title={isCameraActive ? "Webcam tracking active • Click to turn off" : "Turn on Webcam Body Tracking"}
            aria-label="Toggle Webcam Motion Tracking"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={2} 
              stroke="currentColor" 
              className="w-4 h-4 shrink-0"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            <span className="hidden sm:inline whitespace-nowrap font-bold">
              {isCameraActive ? 'Camera ON' : 'Camera'}
            </span>
          </button>

          {/* Motion Guide Button */}
          <button
            id="motion-guide-btn"
            onClick={onOpenMotionGuide}
            className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-full bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white border border-white/15 transition-all duration-200 active:scale-95 text-xs sm:text-sm font-semibold shrink-0 min-h-[44px] min-w-[44px] cursor-pointer"
            title="Motion to Music Guide (Physical Mapping)"
            aria-label="Open Motion to Music Guide"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={2} 
              stroke="currentColor" 
              className="w-4 h-4 text-sky-400 shrink-0"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
            </svg>
            <span className="hidden md:inline whitespace-nowrap font-bold">Guide</span>
          </button>

          {/* Zen Focus Mode Toggle */}
          <button
            id="zen-mode-toggle-btn"
            onClick={onToggleZenMode}
            className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-full bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white border border-white/15 transition-all duration-200 active:scale-95 text-xs sm:text-sm font-semibold shrink-0 min-h-[44px] min-w-[44px] cursor-pointer"
            title="Enter Distraction-Free Zen Mode (Z)"
            aria-label="Enter Distraction-Free Zen Mode"
          >
            <span className="text-sm shrink-0">🌿</span>
            <span className="hidden md:inline whitespace-nowrap font-bold">Zen</span>
          </button>
        </div>
      </div>
    </nav>
  );
};


